import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Typography, makeStyles } from '@material-ui/core';
import StatusIndicator from '../../../statusIndicator';
import { ReactComponent as CheckCircleIcon } from '../../../../../assets/icons/check-circle-outline-green.svg';
import { ReactComponent as ErrorIcon } from '../../../../../assets/icons/close-red-background.svg';
import { LEVELING_AGENT_TOOL_CALL_STATUSES } from '../../../../../constants/levelingAgent';
import {
  getLevelingAgentToolMeta,
  parseCompletedWriteToolResult,
  resolveToolCallStatus,
} from '../../../../../utility/levelingAgent';

const STATUS_ICONS = {
  [LEVELING_AGENT_TOOL_CALL_STATUSES.COMPLETED]: CheckCircleIcon,
  [LEVELING_AGENT_TOOL_CALL_STATUSES.ERROR]: ErrorIcon,
};

const STATUS_DISPLAY = {
  [LEVELING_AGENT_TOOL_CALL_STATUSES.PENDING]: {
    indicatorKey: 'inProgress',
    labelKey: 'running',
  },
  [LEVELING_AGENT_TOOL_CALL_STATUSES.ERROR]: {
    indicatorKey: 'rejected',
    labelKey: 'failed',
  },
  [LEVELING_AGENT_TOOL_CALL_STATUSES.COMPLETED]: {
    indicatorKey: 'done',
    labelKey: 'completed',
  },
};

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.palette.primary.bluish7}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    '& + &': {
      marginTop: theme.spacing(1),
    },
  },
  icon: {
    flexShrink: 0,
    width: 16,
    height: 16,
  },
  name: {
    flex: 1,
    minWidth: 0,
    fontFamily: 'monospace',
    fontSize: 14,
    lineHeight: '20px',
  },
  badge: {
    flexShrink: 0,
    marginLeft: 'auto',
  },
}));

const Tool = ({ translations, tool, onSuccess = undefined }) => {
  const classes = useStyles();
  const notifiedRef = useRef(false);
  const toolName = tool.call?.name ?? 'unknown';

  const toolSignature = `${tool.call?.id ?? ''}:${
    tool.state ?? ''
  }:${resolveToolCallStatus(tool)}`;

  useEffect(() => {
    if (!onSuccess || notifiedRef.current) {
      return;
    }

    const parsed = parseCompletedWriteToolResult(tool);
    if (!parsed) {
      return;
    }

    notifiedRef.current = true;
    onSuccess(parsed);
  }, [toolSignature, onSuccess, tool]);
  const meta = getLevelingAgentToolMeta(toolName);
  const status = resolveToolCallStatus(tool);
  const { indicatorKey, labelKey } = STATUS_DISPLAY[status];
  const StatusIcon = STATUS_ICONS[status];
  const statusLabels = translations.tools?.status ?? {};
  const toolLabels = translations.tools ?? {};
  const displayName = (meta.labelKey && toolLabels[meta.labelKey]) || toolName;
  const statusLabel = statusLabels[labelKey] ?? labelKey;

  return (
    <div className={classes.row}>
      {status === LEVELING_AGENT_TOOL_CALL_STATUSES.PENDING ? (
        <CircularProgress className={classes.icon} size={16} />
      ) : (
        <StatusIcon className={classes.icon} aria-hidden />
      )}
      <Typography className={classes.name} variant="body2" noWrap>
        {displayName}
      </Typography>
      <StatusIndicator
        className={classes.badge}
        status={{ key: indicatorKey, name: statusLabel }}
        isSmall
      />
    </div>
  );
};

Tool.propTypes = {
  translations: PropTypes.object.isRequired,
  tool: PropTypes.shape({
    call: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      args: PropTypes.shape({}),
    }),
    result: PropTypes.shape({}),
    state: PropTypes.string,
  }).isRequired,
  onSuccess: PropTypes.func,
};

export default Tool;
